import type { PortalAsset, PortalAssetReviewStatus } from '../../types/portalAsset.js';
import { assetDisplayName, originalFileLine } from './displayName.js';

// DELETE AND RESTORE ONE ASSET (2026-10-08). PURE — the server applies the plan
// in ONE transaction with the counter move and the audit entry (the review
// transaction's shape).
//
// ⚠️⚠️ WHY THIS EXISTS BEFORE BULK: members consent at upload, and consent is
// withdrawable. Until this, nothing in the system could take an asset out of the
// portal, so a withdrawal had no mechanism at all.
//
// ⚠️⚠️ WHAT A DELETE DOES, AND WHAT IT DOES NOT. It is a SOFT delete:
//   · the asset leaves every live list and stops being usable (isAssetUsable
//     reads `deletedAt`), immediately;
//   · it can be RESTORED by Kyck for RETENTION_DAYS;
//   · ⚠️ the FILE IS NOT REMOVED AT DELETE TIME. The originals and renditions
//     stay in the bucket until the retention purge sweep removes them once the
//     window has elapsed (purgeSweep.ts, 2026-10-09 — a scheduled maintenance
//     endpoint, so it removes them only once its scheduler exists), and a purged
//     object then sits in GCS soft delete for 7 more days
//     (portal-assets-decisions.md);
//   · ⚠️ signed URLs ALREADY ISSUED keep working until their window ends (up to
//     6h, cdnSign.ts CDN_WINDOW_MS), and a CDN edge may go on serving a cached
//     rendition for that long too. Un-listing an asset is not a takedown.
// A genuine takedown (consent withdrawn and the file must be gone now) is NOT
// a delete: it is Purge now (purge.ts, 2026-10-09), which removes the objects,
// invalidates the CDN and records why. The confirmation copy below points
// there, because the person clicking Delete is the person who would otherwise
// believe a takedown was done.
//
// ⚠️ EITHER PARTY MAY DELETE (Scott, 2026-10-08: clubs and Kyck are equal
// authorities). A club may delete any asset in its own scope whatever its review
// status; the UI warns when it is approved, since Kyck may already have used it.
// RESTORE IS KYCK'S ONLY — a club that mis-clicks asks Kyck, and the club's
// confirmation says that before the click.

/**
 * ⚠️⚠️ THE ONE RESTORE WINDOW (Scott, 2026-10-09: shortened from the original longer window). Everything that
 * states, counts down or relies on it derives from this: the delete confirmation,
 * the "days left" line (tile.ts deletedDaysLeft), the Recently-deleted view, the
 * stored `purgeAfter`, and the categories delete guard. A test fails if a bare
 * "N days" literal about it survives in the portal-assets paths.
 *
 * ⚠️⚠️ THE §9 PURGE SWEEP ENFORCES THIS (server/modules/portalAssets/purgeSweep.ts,
 * POST /api/portal-assets/maintenance/purge-sweep, 2026-10-09) — but only once
 * Cloud Scheduler calls it, which is an operator step. Until that scheduler
 * exists and has been seen to fire, a deleted asset's file still stays in the
 * bucket indefinitely. (A takedown never waits for this: see Purge now.)
 * ⚠️ The sweep acts on the LATER of the stored purgeAfter and the recomputed
 * date — see isPurgeableNow.
 */
export const RETENTION_DAYS = 7;
export const DAY_MS = 24 * 60 * 60 * 1000;

/** When a delete at `deletedAtMs` stops being restorable / becomes purgeable. */
export function purgeAfterMs(deletedAtMs: number): number {
  return deletedAtMs + RETENTION_DAYS * DAY_MS;
}

/**
 * Whether the retention sweep may destroy this asset now. PURE.
 *
 * ⚠️⚠️ THE LATER OF TWO DATES, and both must have elapsed: the STORED
 * `deleted.purgeAfter` (stamped at delete time under whatever window applied
 * then — assets deleted before the window was shortened carry the longer one)
 * and the RECOMPUTED `deletedAt + RETENTION_DAYS`. We never destroy a file
 * earlier than anything anyone was told: a club that deleted under the old window
 * was told the old window. Deleting early is unrecoverable; deleting late costs
 * storage. (tile.ts deletedDaysLeft deliberately DISPLAYS the recomputed date
 * only, which under-promises — a harmless direction that must not become the
 * rule for destruction.)
 * ⚠️ Anything unreadable is NOT purgeable: a missing `deleted` block, an
 * unparseable date, or an asset already purged all answer false. A live asset
 * (`deletedAt: null`) is never purgeable by the sweep.
 */
export function isPurgeableNow(
  asset: Pick<PortalAsset, 'deletedAt' | 'deleted' | 'purgedAt'>,
  nowMs: number,
): boolean {
  if (asset.purgedAt !== null) return false;
  if (asset.deletedAt === null || !asset.deleted) return false;
  const deletedAt = Date.parse(asset.deletedAt);
  const stored = Date.parse(asset.deleted.purgeAfter);
  if (!Number.isFinite(deletedAt) || !Number.isFinite(stored)) return false;
  return nowMs >= Math.max(stored, purgeAfterMs(deletedAt));
}

export type DeletionCurrent = Pick<PortalAsset, 'deletedAt' | 'purgedAt'>;

export type DeletePlan = { kind: 'noop' } | { kind: 'apply'; purgeAfterMs: number };

/** Deleting an already-deleted asset writes nothing (a double-click is not a second delete). */
export function planDelete(current: DeletionCurrent, nowMs: number): DeletePlan {
  if (current.deletedAt !== null) return { kind: 'noop' };
  return { kind: 'apply', purgeAfterMs: purgeAfterMs(nowMs) };
}

export type RestorePlan = { kind: 'refuse'; status: 409; message: string } | { kind: 'apply' };

export function planRestore(current: DeletionCurrent): RestorePlan {
  if (current.deletedAt === null) return { kind: 'refuse', status: 409, message: "This asset isn't deleted." };
  return { kind: 'apply' };
}

export type DeleteAudience = 'client' | 'staff';

/**
 * What the confirmation says. ⚠️ Pure and tested, because every clause is a
 * promise someone will act on: "Kyck can restore it" is true only within the
 * window, and "the file is gone" would be untrue today.
 */
export function deleteConfirmation(
  tile: { title?: string | null; originalFilename: string; review: { status: PortalAssetReviewStatus } },
  audience: DeleteAudience,
): { title: string; file: string | null; body: string[]; warning: string | null } {
  // ⚠️ Named by the title the person has been reading everywhere else; the original file is shown beneath (`file`).
  const name = `“${assetDisplayName(tile)}”`;
  const warning =
    tile.review.status === 'approved'
      ? audience === 'client'
        ? 'This one is approved, so Kyck may already have used it. Deleting it stops it being used from now on, but it does not take it down from anything already published — tell your Kyck account manager if that matters.'
        : 'This one is approved and may already be in use. Deleting it stops it being used from now on; it does not take it down from anything already published.'
      : null;
  const body =
    audience === 'client'
      ? [
          `${name} will be removed from your portal straight away and won't be used.`,
          `Kyck can restore it for ${RETENTION_DAYS} days if you change your mind — contact your account manager.`,
        ]
      : [
          `${name} leaves the club's portal straight away and stops being usable. You can restore it from Recently deleted for ${RETENTION_DAYS} days.`,
          "⚠️ The file itself is NOT removed, and links already issued keep working until their window ends. If a member has withdrawn consent and the file must be gone now, use Purge now instead — a delete is not a takedown.",
        ];
  return { title: `Delete ${name}?`, file: originalFileLine(tile), body, warning };
}

/**
 * The line a deleted asset carries in its detail view. ⚠️ It never says the file
 * WILL be removed on a date. The retention purge sweep now exists (purgeSweep.ts)
 * but has not yet been seen to run in production — its scheduler is an operator
 * step — so a date here would still be a promise nothing has yet proved. Revisit
 * the wording once a real scheduled fire has been observed.
 */
export function deletedLine(daysLeft: number | null, audience: DeleteAudience): string | null {
  if (daysLeft === null) return null;
  const left = daysLeft <= 0 ? 'the restore window has closed' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left to restore it`;
  return audience === 'client'
    ? `Deleted — not in use. Ask Kyck if you want it back (${left}).`
    : `Deleted — not usable, hidden from the club (${left}).`;
}
